// src/pages/ListaIngresos.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, functions } from '../firebase';
import { collection, query, where, orderBy, onSnapshot, getDocs, doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import * as XLSX from 'xlsx';
import './GenerarTicket.css';
import './ListaIngresos.css';
import './ConfiguracionRecibos.css';

import ConfirmDeleteModal from '../components/ConfirmDeleteModal';
import ActionMenu from '../components/ActionMenu';
import { FaEnvelope, FaCheckCircle } from 'react-icons/fa';
import { pushToSyncQueue } from '../utils/offlineSyncQueue';
import { useModulos } from '../hooks/useModulos';
import { esPartidaContableResidente } from '../utils/conceptosContables';

const ListaIngresos = () => {
    const [ingresos, setIngresos] = useState([]);

    const [casas, setCasas] = useState([]);
    const [recibosConfig, setRecibosConfig] = useState(null);
    const [conceptosConfig, setConceptosConfig] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showReceipt, setShowReceipt] = useState(false);
    const [ingresoSeleccionado, setIngresoSeleccionado] = useState(null);
    const receiptRef = useRef(null);
    const navigate = useNavigate();
    const location = useLocation();
    const { userData } = useAuth();
    const fraccionamientoId = userData?.fraccionamientoId;
    const { modulos } = useModulos(fraccionamientoId);

    const [filtroCliente, setFiltroCliente] = useState(() => {
        return location.state?.search || '';
    });
    const [filtroFecha, setFiltroFecha] = useState('');
    const [filtroConcepto, setFiltroConcepto] = useState('');
    const [filtroMorosos, setFiltroMorosos] = useState(false);
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmAction, setConfirmAction] = useState(null);
    const [sendingStatus, setSendingStatus] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ open: false, ingresoId: null, isLoading: false });

    // Paginación
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 12;

    useEffect(() => {
        if (!fraccionamientoId) return;
        const ref = collection(db, "fraccionamientos", fraccionamientoId, "ingresos");
        const q = query(ref, orderBy('fecha', 'desc'));
        const unsub = onSnapshot(q, (snapshot) => {
            const list = snapshot.docs
                .map(doc => ({ id: doc.id, ...doc.data() }))
                .filter(doc => !doc.deleted && doc.metodoPago !== 'registro_automatico');
            setIngresos(list);
            setLoading(false);
        });
        return () => unsub();
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!fraccionamientoId) return;
        const loadCasas = async () => {
            try {
                const ref = collection(db, "fraccionamientos", fraccionamientoId, "casas");
                const snapshot = await getDocs(ref);
                setCasas(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            } catch (error) { console.error(error); }
        };
        loadCasas();
    }, [fraccionamientoId]);

    useEffect(() => {
        if (!fraccionamientoId) return;
        const loadConfig = async () => {
            try {
                const snap = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (snap.exists()) {
                    const data = snap.data();
                    setRecibosConfig(data.configuracionRecibos || null);
                    setConceptosConfig(data.configuracionRecibos?.conceptosList || []);
                }
            } catch (error) { console.error(error); }
        };
        loadConfig();
    }, [fraccionamientoId]);

    const formatCurrency = (amount) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(amount || 0);

    const obtenerInfoCasa = (casaId, numeroCasa) => {
        let casa = casas.find(c => c.id === casaId);
        if (!casa && numeroCasa) {
            casa = casas.find(c => c.numero === numeroCasa || c.numero === String(numeroCasa));
        }
        if (casa) {
            const nombre = casa.propietario || casa.nombre_residente || '';
            const numero = casa.numero || 'S/N';
            return nombre ? `Casa ${numero} - ${nombre}` : `Casa ${numero}`;
        }
        if (numeroCasa) return `Casa ${numeroCasa}`;
        return 'N/A';
    };

    let conceptosUnicos = conceptosConfig?.length
        ? conceptosConfig
        : [...new Set(ingresos.map(ing => ing.concepto).filter(Boolean))].filter(c => !c.toLowerCase().includes('saldo final de mes'));

    if (modulos?.consumo_agua_potable === true) {
        const tieneAgua = conceptosUnicos.some(c => c.toLowerCase().includes('agua') || c.toLowerCase().includes('cea'));
        if (!tieneAgua) {
            conceptosUnicos = [...conceptosUnicos, 'CEA, Agua Potable'];
        }
    } else {
        conceptosUnicos = conceptosUnicos.filter(c => !c.toLowerCase().includes('agua') && !c.toLowerCase().includes('cea'));
    }

    const normalizar = (str = '') => str.toString().toLowerCase().trim();

    const ingresosFiltrados = ingresos.filter(ingreso => {
        const conceptoStr = String(ingreso.concepto || ingreso.descripcion || '').toLowerCase();
        const metodoPago = String(ingreso.metodoPago || '').trim();

        // ── REGLA DE CAJA REAL ──────────────────────────────────────────────
        // Solo mostramos movimientos que afecten el flujo de efectivo.
        
        // 1. Bloqueo estricto de banderas visuales que NO son dinero real
        if (conceptoStr.includes('saldo final de mes')) return false;

        // 2. Identificar si es un indicador visual de adeudo (sin pago real o generado por sistema)
        // Si no tiene método de pago o es un método de sistema, asumimos que es una bandera visual.
        const metodoPagoLower = metodoPago.toLowerCase();
        const esMetodoSistema = metodoPagoLower.includes('registro_automatico') || 
                                metodoPagoLower.includes('automatico') || 
                                metodoPagoLower.includes('automático');
                                
        const esIndicadorVisual = !metodoPago || metodoPago === 'N/A' || metodoPago === '' || esMetodoSistema;

        if (esIndicadorVisual) {
            // Filtramos conceptos que suelen usarse como "marcadores de deuda"
            if (conceptoStr.includes('adeudo historico') ||
                conceptoStr.includes('adeudo histórico') ||
                conceptoStr.includes('adeudo cuota de mantenimiento') ||
                conceptoStr.includes('sin adeudo cuota de mantenimiento') ||
                conceptoStr.includes('cuota extraordinaria') ||
                conceptoStr.includes('recargo por morosidad')) {
                return false;
            }
        }

        // Si llegó aquí y tiene método de pago (o no es un marcador conocido), es caja real.

        const casa = casas.find(c => c.id === ingreso.casaId);
        const textoCasa = casa ? `${casa.numero || ''} ${casa.propietario || casa.nombre_residente || ''}` : '';
        const filtroCasa = normalizar(filtroCliente);
        const cumpleFiltroCliente = !filtroCasa ||
            normalizar(textoCasa).includes(filtroCasa) ||
            normalizar(casa?.id).includes(filtroCasa) ||
            normalizar(casa?.numero).includes(filtroCasa);

        const cumpleFiltroFecha = !filtroFecha || (() => {
            if (!ingreso.fecha) return false;
            const [anio, mes, dia] = filtroFecha.split('-').map(Number);
            const fechaFiltroLocal = new Date(anio, mes - 1, dia);
            return ingreso.fecha.toDate().toLocaleDateString('es-MX') === fechaFiltroLocal.toLocaleDateString('es-MX');
        })();

        const cumpleFiltroConcepto = !filtroConcepto || (() => {
            if (filtroConcepto === 'Agua Potable') {
                return conceptoStr.includes('agua') || conceptoStr.includes('cea');
            }
            return ingreso.concepto === filtroConcepto;
        })();

        const deudaCasa = Number(casa?.adeudo || casa?.deudaTotal || casa?.montoAdeudado || 0);
        const flagMorosoCasa = casa?.moroso === true || casa?.esMoroso === true;
        const esMorosoIngreso = ingreso.esMoroso === true;
        const esCasaMorosa = esMorosoIngreso || flagMorosoCasa || deudaCasa > 0;
        const cumpleFiltroMorosos = !filtroMorosos || esCasaMorosa;

        return cumpleFiltroCliente && cumpleFiltroFecha && cumpleFiltroConcepto && cumpleFiltroMorosos;
    });

    useEffect(() => {
        setCurrentPage(1);
    }, [filtroCliente, filtroFecha, filtroConcepto, filtroMorosos]);

    // Calcular índices para paginación
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    const currentItems = ingresosFiltrados
        .slice()
        .sort((a, b) => {
            const da = (a.timestamp?.toDate ? a.timestamp.toDate() : (a.timestamp ? new Date(a.timestamp) : null))
                || (a.fecha?.toDate ? a.fecha.toDate() : (a.fecha ? new Date(a.fecha) : null))
                || new Date(0);
            const db = (b.timestamp?.toDate ? b.timestamp.toDate() : (b.timestamp ? new Date(b.timestamp) : null))
                || (b.fecha?.toDate ? b.fecha.toDate() : (b.fecha ? new Date(b.fecha) : null))
                || new Date(0);
            return db - da;
        })
        .slice(indexOfFirstItem, indexOfLastItem);
    const totalPages = Math.ceil(ingresosFiltrados.length / itemsPerPage);

    const handlePageChange = (pageNumber) => {
        setCurrentPage(pageNumber);
    };

    // Exportar a Excel con TODOS los datos filtrados (no solo la página actual)
    const exportarExcel = () => {
        const datos = ingresosFiltrados.map(ing => ({
            'Folio': ing.folio || 'N/A',
            'Fecha': ing.fecha?.toDate?.().toLocaleDateString('es-MX') || 'N/A',
            'Casa': obtenerInfoCasa(ing.casaId, ing.numeroCasa),
            'Concepto': ing.concepto || 'N/A',
            'Método': ing.metodoPago || '---',
            'Referencia': ing.referencia || '---',
            'Monto': ing.monto || 0,
            'Descuento': ing.descuento || 0,
            'Neto': ing.montoNeto || ing.monto || 0
        }));
        const ws = XLSX.utils.json_to_sheet(datos);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Ingresos');
        XLSX.writeFile(wb, `Ingresos_${new Date().toLocaleDateString('es-MX')}.xlsx`);
    };

    const handleSendEmail = async () => {
        setSendingStatus('loading');
        try {
            const casa = casas.find(c => c.id === ingresoSeleccionado.casaId);
            const email = casa?.email || casa?.correo;
            if (!email) { setSendingStatus({ type: 'error', msg: 'Sin correo' }); return; }

            const payloadToSend = {
                email, fraccId: fraccionamientoId, receipt: ingresoSeleccionado, config: recibosConfig, casaInfo: { ...casa, fraccionamientoId }
            };

            if (!navigator.onLine) {
                pushToSyncQueue('sendReceiptEmail', payloadToSend);
                setSendingStatus({ type: 'success', msg: 'Guardado para envío offline' });
                setTimeout(() => { setShowConfirmModal(false); setSendingStatus(null); }, 2000);
                return;
            }

            const res = await httpsCallable(functions, 'sendReceiptEmail')(payloadToSend);
            if (res.data.success) {
                setSendingStatus({ type: 'success', msg: 'Enviado' });
                setTimeout(() => { setShowConfirmModal(false); setSendingStatus(null); }, 2000);
            } else { setSendingStatus({ type: 'error', msg: res.data.message }); }
        } catch (e) { setSendingStatus({ type: 'error', msg: e.message }); }
    };

    const handleDeleteIngreso = (ingresoId) => setDeleteConfirm({ open: true, ingresoId, isLoading: false });

    const handleConfirmDeleteIngreso = async () => {
        try {
            const ingresoRef = doc(db, 'fraccionamientos', fraccionamientoId, 'ingresos', deleteConfirm.ingresoId);
            const ingresoSnap = await getDoc(ingresoRef);
            if (ingresoSnap.exists()) {
                const ingData = ingresoSnap.data();
                const desglose = ingData.desgloseConceptos && Array.isArray(ingData.desgloseConceptos) ? ingData.desgloseConceptos : [ingData];
                
                for (const item of desglose) {
                    const conceptoLower = String(item.concepto || ingData.concepto || '').toLowerCase();
                    const isAgua = conceptoLower.includes('agua') || conceptoLower.includes('cea') || item.periodoAguaId || ingData.periodoAguaId;
                    
                    if (isAgua) {
                        const periodoId = item.periodoAguaId || ingData.periodoAguaId;
                        if (periodoId) {
                            try {
                                const aguaRef = doc(db, 'fraccionamientos', fraccionamientoId, 'consumosAguaPotable', periodoId);
                                const aguaSnap = await getDoc(aguaRef);
                                if (aguaSnap.exists()) {
                                    const aguaData = aguaSnap.data();
                                    const cIdNorm = String(ingData.casaId || '').trim().toUpperCase();
                                    const cNumNorm = String(ingData.casaNumero || ingData.numeroCasa || '').trim().toUpperCase();
                                    const desgloseCasasNuevo = (aguaData.desgloseCasas || []).map(c => {
                                        const matchId = c.casaId && String(c.casaId).trim().toUpperCase() === cIdNorm;
                                        const matchNum = c.casaNumero && String(c.casaNumero).trim().toUpperCase() === cNumNorm;
                                        if (matchId || matchNum) {
                                            return { ...c, pagado: false, estadoPago: 'PENDIENTE' };
                                        }
                                        return c;
                                    });
                                    await updateDoc(aguaRef, { desgloseCasas: desgloseCasasNuevo });
                                }
                            } catch (aguaErr) {
                                console.warn('Error revirtiendo periodo en consumosAguaPotable:', aguaErr);
                            }
                        }

                        // También restaurar mesesPendientes de agua de la vivienda
                        const targetCasaId = ingData.casaId;
                        if (targetCasaId) {
                            try {
                                const qAgua = query(collection(db, 'fraccionamientos', fraccionamientoId, 'casas', targetCasaId, 'mesesPendientes'), where('tipo', '==', 'adeudo_agua'));
                                const snapAguaMeses = await getDocs(qAgua);
                                for (const mesDoc of snapAguaMeses.docs) {
                                    const mData = mesDoc.data();
                                    if (mData.estado !== 'pendiente') {
                                        await updateDoc(mesDoc.ref, {
                                            estado: 'pendiente',
                                            montoPagado: 0,
                                            montoPendiente: Number(mData.montoOriginal || mData.cargoTotal || mData.monto || 0)
                                        });
                                    }
                                }
                            } catch (mesErr) {
                                console.warn('Error revirtiendo mesesPendientes de agua:', mesErr);
                            }
                        }
                    }
                }
            }
            await updateDoc(ingresoRef, { deleted: true, deletedAt: serverTimestamp() });
            setDeleteConfirm({ open: false, ingresoId: null, isLoading: false });
        } catch (err) { console.error(err); }
    };



    const renderReceipt = (ingreso) => {
        const casa = casas.find(c => c.id === ingreso.casaId) || {};
        const cfg = recibosConfig || {};
        const formatMoney = (n) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: cfg.moneda || 'MXN' }).format(n || 0);
        const total = ingreso.montoNeto || ingreso.monto || 0;
        // Parsear fecha local correctamente sin conversión UTC
        const parseLocal = (dateStr) => {
            if (!dateStr || typeof dateStr !== 'string') return new Date();
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
        };
        // Convierte URL privada de Storage a URL pública (quita token, fuerza ?alt=media)
        const toPublicUrl = (url) => {
            if (!url || typeof url !== 'string') return '';
            try {
                const p = new URL(url);
                if (!p.hostname.includes('firebasestorage')) return url;
                p.search = '?alt=media';
                return p.toString();
            } catch {
                return url.split('?')[0] + '?alt=media';
            }
        };

        const numeroALetras = (num = 0) => {
            const unidades = ['CERO', 'UNO', 'DOS', 'TRES', 'CUATRO', 'CINCO', 'SEIS', 'SIETE', 'OCHO', 'NUEVE'];
            const decenas = ['DIEZ', 'ONCE', 'DOCE', 'TRECE', 'CATORCE', 'QUINCE', 'DIECISÉIS', 'DIECISIETE', 'DIECIOCHO', 'DIECINUEVE'];
            const tens = ['', 'DIEZ', 'VEINTE', 'TREINTA', 'CUARENTA', 'CINCUENTA', 'SESENTA', 'SETENTA', 'OCHENTA', 'NOVENTA'];
            const centenas = ['', 'CIENTO', 'DOSCIENTOS', 'TRESCIENTOS', 'CUATROCIENTOS', 'QUINIENTOS', 'SEISCIENTOS', 'SETECIENTOS', 'OCHOCIENTOS', 'NOVECIENTOS'];
            const toWords = (n) => {
                let w = '';
                const c = Math.floor(n / 100); const d = Math.floor((n % 100) / 10); const u = n % 10;
                if (c) w += (c === 1 && d === 0 && u === 0 ? 'CIEN' : centenas[c]) + ' ';
                if (d === 1) w += decenas[u] + ' ';
                else if (d === 2) w += (u === 0 ? 'VEINTE' : 'VEINTI' + unidades[u].toLowerCase()) + ' ';
                else if (d > 2) { w += tens[d]; if (u) w += ' Y ' + unidades[u]; w += ' '; }
                else if (u) w += unidades[u] + ' ';
                return w.trim();
            };
            const miles = Math.floor(num / 1000); const resto = num % 1000;
            let res = '';
            if (miles) res += (miles === 1 ? 'MIL' : toWords(miles) + ' MIL') + ' ';
            if (resto || num === 0) res += toWords(resto);
            return res.trim();
        };
        const totalLetras = `${numeroALetras(Math.floor(total))} ${cfg.moneda === 'USD' ? 'DÓLARES' : 'PESOS MEXICANOS'} ${(total % 1).toFixed(2).split('.')[1] || '00'}/100`;

        return (
            <div className="receipt-preview">
                <div className="preview-row preview-top">
                    <div>
                        {cfg.logoUrl ? <img src={toPublicUrl(cfg.logoUrl)} alt="Logo" className="preview-logo" /> : <div className="preview-logo placeholder">Logo</div>}
                        <div className="preview-condo"><h4>{cfg.condominioNombre || 'Condominio'}</h4><p>{cfg.condominioDireccion || ''}</p></div>
                    </div>
                    <div className="preview-right">
                        <h3 className="preview-title">RECIBO DE PAGO</h3>
                        <div className="preview-meta">
                            <span>Folio: {ingreso.folio || '---'}</span>
                            <span>Fecha de pago: {ingreso.fechaPagoOriginal ? (() => {
                                const [y, m, d] = ingreso.fechaPagoOriginal.split('T')[0].split('-');
                                return `${d}/${m}/${y}`;
                            })() : ingreso.fecha?.toDate?.().toLocaleDateString('es-MX')}</span>
                            <span>Fecha de emisión: {ingreso.fecha?.toDate?.().toLocaleDateString('es-MX')} {ingreso.fecha?.toDate?.().toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' })}</span>
                        </div>
                    </div>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Datos de Facturación</h5>
                    <p><strong>Razón Social:</strong> {cfg.razonSocial || '---'}</p>
                    <p><strong>RFC:</strong> {cfg.rfc || '---'}</p>
                    {cfg.datosFacturacion && <p className="preview-muted">{cfg.datosFacturacion}</p>}
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Administrador</h5>
                    <p><strong>Nombre:</strong> {cfg.administradorNombre || '---'}</p>
                    <p><strong>Dirección:</strong> {cfg.adminDireccion || '---'}</p>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Residente</h5>
                    <p><strong>Casa:</strong> {casa.numero || '---'}</p>
                    <p><strong>Nombre:</strong> {casa.propietario || casa.nombre_residente || '---'}</p>
                    <p><strong>Email:</strong> {casa.email || '---'}</p>
                    <p><strong>Teléfono:</strong> {casa.telefono || '---'}</p>
                </div>
                <hr className="preview-divider" />
                <div className="preview-metodo-pago">
                    <div className="preview-block">
                        <p><strong>Método de Pago:</strong> {ingreso.metodoPago || '---'}</p>
                        <p><strong>Referencia:</strong> {ingreso.referencia || '---'}</p>
                    </div>
                </div>
                <hr className="preview-divider" />
                <div className="preview-block">
                    <h5>Conceptos de Pago</h5>
                    <div className="preview-table">
                        <div className="preview-table-header"><span>Concepto</span><span>Importe</span></div>
                        {(() => {
                            const conceptoLower = String(ingreso.concepto || '').toLowerCase();
                            const esPagoAnual = conceptoLower.includes('anual') || ingreso.esPagoAnual;
                            // Cuota de referencia = importe máximo del desglose (un mes completo)
                            const cuotaRef = Array.isArray(ingreso.desgloseMensual) && ingreso.desgloseMensual.length > 0
                                ? Math.max(...ingreso.desgloseMensual.map(d => Number(d.importe || 0)))
                                : 0;

                            const partidasArray = (Array.isArray(ingreso.pagos) && ingreso.pagos.length > 0)
                                ? ingreso.pagos
                                : (Array.isArray(ingreso.desgloseConceptos) && ingreso.desgloseConceptos.length > 0)
                                    ? ingreso.desgloseConceptos
                                    : null;

                            if (partidasArray) {
                                return partidasArray.map((item, idx) => {
                                    let detalleText = item.detalle;
                                    if (!detalleText && Array.isArray(item.desgloseMensual) && item.desgloseMensual.length > 0) {
                                        detalleText = item.desgloseMensual.map(d => `${d.mes} ${d.año || d.anio || ''}${d.esParcial ? ' (Parcial)' : ''}`).join(', ');
                                    }
                                    return (
                                        <div key={idx} className="preview-table-row">
                                            <span>{item.concepto} {detalleText ? `· ${detalleText}` : ''}</span>
                                            <span>{formatMoney(item.montoNeto || item.monto)}</span>
                                        </div>
                                    );
                                });
                            }

                            if (Array.isArray(ingreso.desgloseMensual) && ingreso.desgloseMensual.length > 0) {
                                const nombreConcepto = esPagoAnual
                                    ? 'Cuota de Mantenimiento'
                                    : (ingreso.concepto || 'Cuota de Mantenimiento');

                                // Mostrar TODOS los meses; marcar parciales por importe o campo esParcial
                                return ingreso.desgloseMensual.map((item, idx) => {
                                    const anioStr = item.año || item.anio || item.year || '';
                                    const esParcialItem = item.esParcial === true || (cuotaRef > 0 && Number(item.importe) < cuotaRef);
                                    const sufijoEstado = esParcialItem ? ' · Pagado Parcial' : ' · Pagado';
                                    return (
                                        <div key={idx} className="preview-table-row">
                                            <span>{nombreConcepto} · {item.mes} {anioStr}{sufijoEstado}</span>
                                            <span>{formatMoney(item.importe)}</span>
                                        </div>
                                    );
                                });
                            }
                            // Fallback: mesesAplicados (versiones anteriores)
                            if (ingreso.mesesAplicados && ingreso.mesesAplicados.length > 0) {
                                return ingreso.mesesAplicados.map((item, idx) => {
                                    const labelEstado = item.estado === 'pagado_completo' ? 'Pagado' : (item.estado === 'pagado_parcialmente' ? 'Pagado Parcial' : 'Abonado');
                                    return (
                                        <div key={idx} className="preview-table-row">
                                            <span>{ingreso.concepto || 'Cuota de Mantenimiento'} · {item.nombreMes} {item.anio} · {labelEstado}</span>
                                            <span>{formatMoney(item.montoAplicado)}</span>
                                        </div>
                                    );
                                });
                            }
                            // Concepto normal sin desglose
                            return (
                                <div className="preview-table-row">
                                    <span>{ingreso.concepto}</span>
                                    <span>{formatMoney(ingreso.monto)}</span>
                                </div>
                            );
                        })()}
                    </div>
                </div>
                {/* Sección de Observaciones — solo si hay texto */}
                {ingreso.observaciones && ingreso.observaciones.trim() && (
                    <>
                        <hr className="preview-divider" />
                        <div className="preview-block">
                            <h5>Observaciones</h5>
                            <p style={{ fontSize: '13px', color: '#374151', whiteSpace: 'pre-wrap' }}>{ingreso.observaciones}</p>
                        </div>
                    </>
                )}
                {(() => {
                    const aplicadoAIntereses = Number(ingreso.aplicadoAIntereses || 0);
                    const aplicadoACapital = Number(ingreso.aplicadoACapital || 0);
                    const aplicadoAAgua = Number(ingreso.aplicadoAAgua || 0);
                    const aplicadoAExtras = Number(ingreso.aplicadoAExtras || 0);
                    const aplicadoAMultas = Number(ingreso.aplicadoAMultas || 0);

                    const adeudoInteresesNuevo = Number(ingreso.adeudoInteresesNuevo ?? 0);
                    const adeudoCapitalNuevo = Number(ingreso.adeudoCapitalNuevo ?? 0);
                    const adeudoAguaNuevo = Number(ingreso.adeudoAguaNuevo ?? 0);
                    const cuotasExtrasNuevo = Number(ingreso.cuotasExtrasNuevo ?? 0);
                    const adeudoMultasNuevo = Number(ingreso.adeudoMultasNuevo ?? 0);
                    const saldoFavorNuevo = Number(ingreso.saldoFavorNuevo ?? 0);

                    const adeudoNuevo = Number(ingreso.adeudoNuevo || 0);
                    const saldoFavorGenerado = Number(ingreso.saldoFavorGenerado || 0);

                    const esTesoreriaRecibo = !esPartidaContableResidente(ingreso);
                    if (esTesoreriaRecibo) {
                        return (
                            <>
                                <hr className="preview-divider" />
                                <div className="preview-block" style={{ backgroundColor: '#fefce8', border: '1px solid #fef08a', borderRadius: '8px', padding: '12px', margin: '10px 0' }}>
                                    <h5 style={{ margin: '0 0 6px 0', color: '#854d0e', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                        💼 Ingreso de Tesorería
                                    </h5>
                                    <p style={{ margin: 0, fontSize: '13px', color: '#713f12', lineHeight: '1.4' }}>
                                        Este comprobante ampara conceptos de tesorería general del condominio. No afecta ni amortiza el adeudo de mantenimiento de la vivienda ni genera saldo a favor.
                                    </p>
                                </div>
                            </>
                        );
                    }

                    const tieneDesglose = aplicadoAIntereses > 0 || aplicadoACapital > 0 || aplicadoAAgua > 0 || aplicadoAExtras > 0 || aplicadoAMultas > 0 || adeudoNuevo > 0 || saldoFavorGenerado > 0 || adeudoInteresesNuevo > 0 || adeudoCapitalNuevo > 0 || adeudoAguaNuevo > 0 || cuotasExtrasNuevo > 0 || adeudoMultasNuevo > 0 || saldoFavorNuevo > 0;
                    if (!tieneDesglose) return null;
                    return (
                        <>
                            <hr className="preview-divider" />
                            <div className="preview-block">
                                <h5>Aplicación del Pago</h5>
                                <div className="preview-table">
                                    <div className="preview-table-header"><span>Detalle</span><span>Monto</span></div>
                                    {aplicadoAIntereses > 0 && <div className="preview-table-row"><span>Pago a intereses</span><span>{formatMoney(aplicadoAIntereses)}</span></div>}
                                    {aplicadoACapital > 0 && <div className="preview-table-row"><span>Pago a capital</span><span>{formatMoney(aplicadoACapital)}</span></div>}
                                    {aplicadoAAgua > 0 && <div className="preview-table-row"><span>Pago a Agua Potable</span><span>{formatMoney(aplicadoAAgua)}</span></div>}
                                    {aplicadoAExtras > 0 && <div className="preview-table-row"><span>Pago a Cuotas Extraordinarias</span><span>{formatMoney(aplicadoAExtras)}</span></div>}
                                    {aplicadoAMultas > 0 && <div className="preview-table-row"><span>Pago a Sanciones Admin.</span><span>{formatMoney(aplicadoAMultas)}</span></div>}

                                    {/* Desglose especial para pagos anticipo con saldoFavor */}
                                    {ingreso.esPagoAnticipo && saldoFavorGenerado > 0 ? (() => {
                                        // Calcular saldo anterior: saldo final - lo que generó este pago
                                        const sfNuevo = Number(ingreso.saldoFavorNuevo || 0);
                                        const sfAnterior = Math.max(0, sfNuevo - saldoFavorGenerado);
                                        return (
                                            <>
                                                {sfAnterior > 0 && (
                                                    <div className="preview-table-row" style={{ color: '#059669' }}>
                                                        <span>Saldo a favor</span>
                                                        <span>{formatMoney(sfAnterior)}</span>
                                                    </div>
                                                )}
                                                <div className="preview-table-row" style={{ color: '#059669' }}>
                                                    <span>Nuevo pago ({ingreso.concepto})</span>
                                                    <span>+ {formatMoney(ingreso.montoNeto || ingreso.monto)}</span>
                                                </div>
                                                <div className="preview-table-row" style={{ color: '#059669', fontWeight: 700 }}>
                                                    <span>Total saldo a favor</span>
                                                    <span>{formatMoney(sfNuevo || (sfAnterior + (ingreso.montoNeto || ingreso.monto || 0)))}</span>
                                                </div>
                                            </>
                                        );
                                    })()
                                        : saldoFavorGenerado > 0 ? (
                                            <div className="preview-table-row" style={{ color: '#059669' }}>
                                                <span>Saldo a favor generado</span>
                                                <span>{formatMoney(saldoFavorGenerado)}</span>
                                            </div>
                                        ) : null}

                                    {/* Desglose de adeudos restantes por concepto */}
                                    {adeudoCapitalNuevo > 0 && <div className="preview-table-row" style={{ color: '#c2410c' }}><span>Adeudo restante (capital)</span><span>{formatMoney(adeudoCapitalNuevo)}</span></div>}
                                    {adeudoInteresesNuevo > 0 && <div className="preview-table-row" style={{ color: '#c2410c' }}><span>Adeudo restante (intereses)</span><span>{formatMoney(adeudoInteresesNuevo)}</span></div>}
                                    {adeudoAguaNuevo > 0 && <div className="preview-table-row" style={{ color: '#0e7490' }}><span>Adeudo restante (Agua Potable)</span><span>{formatMoney(adeudoAguaNuevo)}</span></div>}
                                    {cuotasExtrasNuevo > 0 && <div className="preview-table-row" style={{ color: '#6d28d9' }}><span>Adeudo restante (Cuotas Extraordinarias)</span><span>{formatMoney(cuotasExtrasNuevo)}</span></div>}
                                    {adeudoMultasNuevo > 0 && <div className="preview-table-row" style={{ color: '#b45309' }}><span>Adeudo restante (Sanciones Admin.)</span><span>{formatMoney(adeudoMultasNuevo)}</span></div>}

                                    {/* Totales y Estados Financieros Finales */}
                                    {saldoFavorNuevo > 0 && (
                                        <div className="preview-table-row" style={{ color: '#059669', fontWeight: 700 }}>
                                            <span>Saldo a favor restante</span>
                                            <span>{formatMoney(saldoFavorNuevo)}</span>
                                        </div>
                                    )}
                                    {adeudoNuevo > 0 && (
                                        <div className="preview-table-row" style={{ color: '#b91c1c', fontWeight: 700 }}>
                                            <span>Adeudo total restante</span>
                                            <span>{formatMoney(adeudoNuevo)}</span>
                                        </div>
                                    )}
                                    {adeudoNuevo === 0 && saldoFavorNuevo === 0 && (
                                        <div className="preview-table-row" style={{ color: '#16a34a', fontWeight: 700 }}>
                                            <span>Estado financiero</span>
                                            <span>¡Al Corriente! ✓</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </>
                    );
                })()}
                <hr className="preview-divider" />
                <div className="preview-total"><span>TOTAL</span><span className="preview-total-amount">{formatMoney(total)}</span></div>
                <p className="preview-leyenda" style={{ fontSize: '10px', textTransform: 'uppercase', fontWeight: '600' }}>SON: {totalLetras}</p>
                {
                    (ingreso.esPagoAnual || ingreso.esPagoAnticipo) && (
                        <p className="preview-disclaimer" style={{
                            fontSize: '11px',
                            fontWeight: '700',
                            textAlign: 'center',
                            margin: '16px 0',
                            padding: '12px',
                            backgroundColor: '#fef3c7',
                            border: '2px solid #f59e0b',
                            borderRadius: '4px',
                            color: '#92400e'
                        }}>
                            EL PAGO DE ESTE RECIBO NO LO LIBERA DE ADEUDOS ANTERIORES, CUALQUIER DUDA O ACLARACIÓN FAVOR DE COMUNICARSE CON LA ADMINISTRACIÓN.
                        </p>
                    )
                }
                <div className="preview-footer">
                    <div className="preview-firma-stack">
                        {cfg.selloUrl && <img src={toPublicUrl(cfg.selloUrl)} alt="Sello" className="preview-sello" />}
                        <div className="preview-firma"><div className="preview-firma-line" /><small>Firma y Sello</small><span>{cfg.administradorNombre || '---'}</span></div>
                    </div>
                </div>
            </div >
        );
    };

    return (
        <div className="generar-ticket-page ingresos-page">
            <div className="lista-egresos-header">
                <button onClick={() => navigate('/finanzas')} className="btn-back-simple">← Volver</button>
                <h2>Historial de Ingresos</h2>
                <button className="btn-exportar-excel" onClick={exportarExcel} disabled={ingresosFiltrados.length === 0}>
                    📊 Exportar a Excel
                </button>
            </div>

            {/* Resumen arriba */}
            <div className="ingresos-resumen ingresos-resumen-top">
                <div className="resumen-item">
                    <span>Total Registros:</span>
                    <strong>{ingresosFiltrados.length}</strong>
                </div>
                <div className="resumen-item">
                    <span>Total Monto:</span>
                    <strong>{formatCurrency(ingresosFiltrados.reduce((sum, ing) => sum + (ing.monto || 0), 0))}</strong>
                </div>
                <div className="resumen-item">
                    <span>Total Descuentos:</span>
                    <strong>{formatCurrency(ingresosFiltrados.reduce((sum, ing) => sum + (ing.descuento || 0), 0))}</strong>
                </div>
                <div className="resumen-item">
                    <span>Total Neto:</span>
                    <strong>{formatCurrency(ingresosFiltrados.reduce((sum, ing) => sum + (ing.montoNeto || ing.monto || 0), 0))}</strong>
                </div>
            </div>

            {/* Barra de Filtros */}
            <div className="filtros-container">
                <div className="filtro-grupo">
                    <label>Buscar por Casa:</label>
                    <input
                        type="text"
                        value={filtroCliente}
                        onChange={(e) => setFiltroCliente(e.target.value)}
                        placeholder="Ej: 221 o Wong"
                        autoComplete="off"
                    />
                </div>

                <div className="filtro-grupo">
                    <label>Buscar por Fecha:</label>
                    <input
                        type="date"
                        value={filtroFecha}
                        onChange={(e) => setFiltroFecha(e.target.value)}
                    />
                </div>

                <div className="filtro-grupo">
                    <label>Buscar por Concepto:</label>
                    <select value={filtroConcepto} onChange={(e) => setFiltroConcepto(e.target.value)}>
                        <option value="">Todos los conceptos</option>
                        {conceptosUnicos.map(concepto => (
                            <option key={concepto} value={concepto}>{concepto}</option>
                        ))}
                    </select>
                </div>

                <div className="filtro-grupo checkbox">
                    <label>
                        <input
                            type="checkbox"
                            checked={filtroMorosos}
                            onChange={(e) => setFiltroMorosos(e.target.checked)}
                        />
                        Solo Morosos
                    </label>
                    <button
                        className="btn-limpiar-filtros"
                        onClick={() => {
                            setFiltroCliente('');
                            setFiltroFecha('');
                            setFiltroConcepto('');
                            setFiltroMorosos(false);
                        }}
                        title="Limpiar todos los filtros"
                    >
                        🔄 Limpiar
                    </button>
                </div>
            </div>

            <div className="lista-ingresos-content">
                {loading ? (
                    <p style={{ textAlign: 'center' }}>Cargando...</p>
                ) : currentItems.length > 0 ? (
                    <div className="tabla-ingresos tabla-ingresos-completa" style={{ maxHeight: '600px', overflowY: 'auto' }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Casa</th>
                                    <th>Concepto</th>
                                    <th>Método de Pago</th>
                                    <th>Referencia</th>
                                    <th>Monto</th>
                                    <th>Descuento</th>
                                    <th>Neto</th>
                                    <th>Acciones</th>
                                </tr>
                            </thead>
                            <tbody>
                                {currentItems.flatMap((item, idx) => {
                                    const dateObj = (item.timestamp?.toDate ? item.timestamp.toDate() : (item.timestamp ? new Date(item.timestamp) : null))
                                        || (item.fecha?.toDate ? item.fecha.toDate() : (item.fecha ? new Date(item.fecha) : null));
                                    const dateLabel = dateObj ? dateObj.toLocaleDateString('es-MX') : 'N/A';
                                    const timeLabel = dateObj ? dateObj.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';
                                    
                                    const esRegistroMensual = item.esRegistroMensual === true;
                                    const hasExtras = esRegistroMensual && item.cuotasExtraniordinarias && item.cuotasExtraniordinarias.length > 0;
                                    
                                    // Monto base: si es registro mensual con extras, mostramos solo la cuota mensual (o el total si no hay desglose)
                                    const montoBase = hasExtras ? (item.cuotaMensual || item.monto || 0) : (item.monto || 0);
                                    const netoBase = hasExtras ? (item.cuotaMensual || item.montoNeto || item.monto || 0) : (item.montoNeto || item.monto || 0);

                                    const rowBase = (
                                        <tr key={`${item.id}-base`}>
                                            <td>
                                                {dateLabel}
                                                {timeLabel ? <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '2px' }}>{timeLabel}</div> : null}
                                            </td>
                                            <td className="casa-info">{obtenerInfoCasa(item.casaId, item.numeroCasa)}</td>
                                            <td className="texto-extendido">
                                                {item.concepto || "N/A"}
                                                {!esPartidaContableResidente(item) && (
                                                    <span style={{
                                                        marginLeft: '6px',
                                                        fontSize: '0.72rem',
                                                        padding: '2px 6px',
                                                        borderRadius: '4px',
                                                        backgroundColor: '#fef3c7',
                                                        color: '#b45309',
                                                        fontWeight: 600,
                                                        display: 'inline-block'
                                                    }}>
                                                        💼 Tesorería
                                                    </span>
                                                )}
                                            </td>
                                            <td>{item.metodoPago || "N/A"}</td>
                                            <td className="texto-extendido">{item.referencia || "N/A"}</td>
                                            <td className="monto-ingreso">{formatCurrency(montoBase)}</td>
                                            <td className="descuento-info">{item.descuentoPorcentaje ? `${item.descuentoPorcentaje}% (-${formatCurrency(item.descuento)})` : 'N/A'}</td>
                                            <td className="monto-neto">{formatCurrency(netoBase)}</td>
                                            <td className="ingreso-actions-cell">
                                                <div className="ingreso-actions-wrapper">
                                                    {item.emailEnviado && (
                                                        <span className="ingreso-verified-icon" title="Recibo enviado por correo">
                                                            <FaEnvelope className="icon-envelope" />
                                                            <FaCheckCircle className="icon-check" />
                                                        </span>
                                                    )}
                                                    <ActionMenu
                                                        onView={() => { setIngresoSeleccionado(item); setShowReceipt(true); }}
                                                        onDelete={() => handleDeleteIngreso(item.id)}
                                                        extraActions={[
                                                            {
                                                                label: 'Enviar por correo',
                                                                onClick: () => {
                                                                    setIngresoSeleccionado(item);
                                                                    setShowConfirmModal(true);
                                                                },
                                                                icon: <FaEnvelope />
                                                            }
                                                        ]}
                                                    />
                                                </div>
                                            </td>
                                        </tr>
                                    );

                                    const rows = [rowBase];

                                    if (hasExtras) {
                                        item.cuotasExtraniordinarias.forEach((extra, eIdx) => {
                                            rows.push(
                                                <tr key={`${item.id}-ext-${eIdx}`} style={{ backgroundColor: '#f9fafb' }}>
                                                    <td>
                                                        <div style={{ color: '#9ca3af', fontSize: '0.8rem' }}>{dateLabel}</div>
                                                    </td>
                                                    <td className="casa-info" style={{ color: '#9ca3af' }}>{obtenerInfoCasa(item.casaId, item.numeroCasa)}</td>
                                                    <td className="texto-extendido">
                                                        <div style={{ fontWeight: 500, color: '#3b82f6', fontSize: '0.9rem' }}>
                                                            ↳ Cuota Extra: {extra.descripcion}
                                                        </div>
                                                    </td>
                                                    <td style={{ color: '#9ca3af' }}>Automático</td>
                                                    <td className="texto-extendido" style={{ color: '#9ca3af' }}>
                                                        EXT-{item.referencia?.split('-')[2] || new Date().getFullYear()}
                                                    </td>
                                                    <td className="monto-ingreso">{formatCurrency(extra.monto)}</td>
                                                    <td className="descuento-info">N/A</td>
                                                    <td className="monto-neto">{formatCurrency(extra.monto)}</td>
                                                    <td className="ingreso-actions-cell">
                                                        {/* No actions for extra rows, they are part of the main receipt */}
                                                    </td>
                                                </tr>
                                            );
                                        });
                                    }

                                    return rows;
                                })}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <p style={{ textAlign: 'center' }}>No se han encontrado ingresos con los filtros aplicados.</p>
                )}

                {/* Paginación */}
                {totalPages > 1 && (
                    <div className="pagination-controls" style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '20px', alignItems: 'center' }}>
                        <button
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1}
                            style={{ padding: '8px 12px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', borderRadius: '6px', border: '1px solid #ccc', background: '#fff' }}
                        >
                            Anterior
                        </button>
                        <span style={{ fontWeight: '600' }}>Página {currentPage} de {totalPages}</span>
                        <button
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages}
                            style={{ padding: '8px 12px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer', borderRadius: '6px', border: '1px solid #ccc', background: '#fff' }}
                        >
                            Siguiente
                        </button>
                    </div>
                )}
            </div>
            {/* Fin de lista-ingresos-content */}

            {
                showReceipt && ingresoSeleccionado && (
                    <div className="modal-overlay receipt-modal">
                        <div className="modal-content receipt-modal-content">
                            <button className="modal-close-btn" onClick={() => setShowReceipt(false)}>✕</button>
                            <div className="receipt-scroll-area">
                                <div className="receipt-preview-container">
                                    <div className="preview-wrapper" ref={receiptRef}>
                                        {renderReceipt(ingresoSeleccionado)}
                                    </div>
                                </div>
                            </div>
                            <div className="receipt-actions">

                                <button type="button" className="btn-secondary" onClick={() => setShowConfirmModal(true)}>
                                    📧 Enviar por correo
                                </button>
                                <button type="button" className="btn-secondary btn-download" onClick={() => {
                                    if (receiptRef.current) {
                                        const w = window.open('', '_blank');
                                        // Copiar todos los estilos del documento actual
                                        const styles = Array.from(document.querySelectorAll('style, link[rel="stylesheet"]'))
                                            .map(style => style.outerHTML)
                                            .join('');

                                        w.document.write(`
                                        <html>
                                            <head>
                                                <title>Recibo de Pago - ${ingresoSeleccionado.folio || ''}</title>
                                                <meta charset="utf-8" />
                                                <meta name="viewport" content="width=device-width, initial-scale=1.0" />
                                                ${styles}
                                                <style>
                                                    body { background-color: white; margin: 0; padding: 20px; }
                                                    /* Forzar la carga de fuentes y estilos antes de imprimir */
                                                </style>
                                            </head>
                                            <body>
                                                <div style="display: flex; justify-content: center;">
                                                    ${receiptRef.current.innerHTML}
                                                </div>
                                                <script>
                                                    window.onload = function() {
                                                        setTimeout(function() {
                                                            window.print();
                                                        }, 500);
                                                    };
                                                </script>
                                            </body>
                                        </html>
                                    `);
                                        w.document.close();
                                    }
                                }}>
                                    ⬇️ Descargar / Imprimir
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }

            {
                showConfirmModal && (
                    <div className="confirm-modal-wrapper">
                        <div className="modal-content confirm-modal-content">
                            {sendingStatus === 'loading' && (
                                <div className="confirm-loading">
                                    <div className="spinner"></div>
                                    <p>Enviando recibo...</p>
                                </div>
                            )}
                            {sendingStatus?.type === 'success' && (
                                <div className="confirm-success">
                                    <div className="success-icon">✓</div>
                                    <p className="confirm-title">¡Enviado exitosamente!</p>
                                    <p className="confirm-msg">{sendingStatus.msg}</p>
                                </div>
                            )}
                            {sendingStatus?.type === 'error' && (
                                <div className="confirm-error">
                                    <div className="error-icon">✕</div>
                                    <p className="confirm-title">Error al enviar</p>
                                    <p className="confirm-msg">{sendingStatus.msg}</p>
                                    <button className="btn-confirm" onClick={() => setSendingStatus(null)}>Intentar nuevamente</button>
                                </div>
                            )}
                            {!sendingStatus && (
                                <>
                                    <h3 className="confirm-title">📧 Enviar por correo</h3>
                                    <p className="confirm-msg">¿Deseas enviar el recibo al correo del residente?</p>
                                    <div className="confirm-actions">
                                        <button className="btn-cancel" onClick={() => setShowConfirmModal(false)}>Cancelar</button>
                                        <button className="btn-confirm" onClick={handleSendEmail}>Confirmar envío</button>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                )
            }



            <ConfirmDeleteModal
                isOpen={deleteConfirm.open}
                title="¿Eliminar ingreso?"
                message="¿Estás seguro de que deseas eliminar este ingreso? Esta acción no se puede deshacer."
                onConfirm={handleConfirmDeleteIngreso}
                onCancel={() => setDeleteConfirm({ open: false, ingresoId: null, isLoading: false })}
                isLoading={deleteConfirm.isLoading}
            />
        </div >
    );
};

export default ListaIngresos;